import { useEffect, useState } from "react";
import { LogOut } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth";
import { cn } from "@/lib/utils";
import type { ShiftState } from "@/hooks/use-labelling";

/**
 * The workstation's top bar: who you are, clocked time, progress against the shift
 * target, and the running action split.
 *
 * The timer ticks locally off the shift's `started_at` rather than polling for
 * elapsed time — a clock that only moves once a minute reads as broken, and the
 * server is the authority on when the shift began, not on what time it is now.
 *
 * **It carries the identity and the sign-out because nothing else can.** This route
 * is chromeless by design, so there is no sidebar to hold the user menu the rest of
 * the app keeps there — without this a labeller has no way to see who they are
 * signed in as and no way to log out at all.
 *
 * **Off shift, this bar goes quiet.** No timer, no progress, and no Start shift
 * button: the off-shift card owns that call to action, and repeating it here gave
 * two identical primary buttons on one screen. A progress bar reading "0 / 500"
 * with an empty track says nothing that "Off shift" has not already said.
 */
export function ShiftBar({
  state,
  onRequestEnd,
  onRequestSignOut,
}: {
  state: ShiftState | null;
  onRequestEnd: () => void;
  /** Signing out ends the shift, so the page decides what that costs — this bar
   * only reports the click (POS2-1093). */
  onRequestSignOut: () => void;
}) {
  const shift = state?.shift ?? null;
  const elapsed = useElapsed(shift?.started_at ?? null);
  const user = useAuthStore((s) => s.user);
  const done = shift?.items_labelled ?? 0;
  const target = shift?.target_items ?? 0;

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-5 border-b border-gray-100 bg-white px-6">
      <div className="flex items-center gap-2.5 whitespace-nowrap">
        <span className="grid h-7 w-7 place-items-center rounded-lg bg-blue-600 text-sm font-bold italic text-white">
          F
        </span>
        <span className="text-sm font-semibold text-gray-900">FoodSight</span>
        <span className="border-l border-gray-200 pl-3 text-xs text-gray-500">Labelling</span>
      </div>

      {shift && (
        <>
          <div className="mx-auto hidden max-w-[440px] flex-1 sm:block">
            <div className="mb-1.5 flex justify-between text-xs text-gray-500">
              <span>Shift progress</span>
              <span>
                <span className="font-medium tabular-nums text-gray-900">{done}</span> / {target}{" "}
                items
              </span>
            </div>
            <div
              className="h-1.5 overflow-hidden rounded-full bg-gray-100"
              role="progressbar"
              aria-valuenow={done}
              aria-valuemin={0}
              aria-valuemax={target}
              aria-label="Shift progress"
            >
              <div
                className="h-full rounded-full bg-blue-600 transition-all"
                style={{ width: `${target ? Math.min(100, (done / target) * 100) : 0}%` }}
              />
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2.5 whitespace-nowrap">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold tabular-nums text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" aria-hidden />
              <span aria-label="Time clocked this shift">{elapsed}</span>
            </span>
            <Button variant="outline" size="sm" onClick={onRequestEnd}>
              End shift
            </Button>
          </div>

          <div className="hidden items-center gap-2 lg:flex">
            <SplitPill tone="emerald" count={shift.correct_count} label="correct" />
            <SplitPill tone="orange" count={shift.relabel_count} label="relabelled" />
            <SplitPill tone="red" count={shift.discard_count} label="discarded" />
            {/* The fourth pill only once there is one, because most shifts never
                produce a flag and a permanent "0 can't verify" reads as a target.
                It has to appear when it does, though: the pills otherwise sum to
                less than the item count in the progress bar beside them. */}
            {shift.cant_verify_count > 0 && (
              <SplitPill tone="neutral" count={shift.cant_verify_count} label="can't verify" />
            )}
          </div>
        </>
      )}

      <div
        className={cn(
          "flex items-center gap-2 whitespace-nowrap border-l border-gray-200 pl-4",
          !shift && "ml-auto",
        )}
      >
        <span className="grid h-7 w-7 place-items-center rounded-full bg-blue-50 text-xs font-medium text-blue-700">
          {initials(user?.user_metadata?.full_name, user?.email)}
        </span>
        <span className="hidden max-w-[220px] truncate text-xs text-gray-500 md:block">
          {user?.email}
        </span>
        <button
          type="button"
          onClick={onRequestSignOut}
          aria-label="Sign out"
          className="rounded-md p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
    </header>
  );
}

function SplitPill({
  tone,
  count,
  label,
}: {
  tone: "emerald" | "orange" | "red" | "neutral";
  count: number;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          tone === "emerald" && "bg-emerald-600",
          tone === "orange" && "bg-orange-500",
          tone === "red" && "bg-red-600",
          tone === "neutral" && "bg-gray-400",
        )}
        aria-hidden
      />
      <span className="tabular-nums">{count}</span> {label}
    </span>
  );
}

/** Initials from the profile name, falling back to the email's local part. */
function initials(fullName: unknown, email: string | undefined): string {
  if (typeof fullName === "string" && fullName.trim()) {
    return fullName
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]!.toUpperCase())
      .join("");
  }
  return (email ?? "?").slice(0, 2).toUpperCase();
}

function useElapsed(startedAt: string | null): string {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  if (!startedAt) return "00:00:00";
  const seconds = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  const hh = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const mm = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}
